import React from "react";
const PT = require("prop-types");
const OpsCart = require("../vault/OpsCart");
const CopyTable = require("../data/CopyTable");
const SelectOptionModal = require("../modals/SelectOptionModal");
const CartItemIcon = require("./CartItemIcon");
const {ChooseOptionModal} = require("../modals/ChooseOptionModal");
const styles = require("./styles/BundleConversionModal.css");

/**
 * Modal for converting unbundled items to a bundle.
 * @param {Object} Cart - The cart object.
 * @param {Function} makeBundle - Callback function to make the bundle.
 * @param {Function} onClose - Callback function to close the modal.
 * @param {Boolean} includeSubscriptionCodes - Whether to include subscription codes in the bundle.
 */
// using a named export here because export default has odd behavior with babel
export const BundleConversionModal = ({
	cart,
	makeBundle,
	onClose,
	includeSubscriptionCodes = false,
	simplified = true,
}) => {
	const availableSubs = OpsCart.getUnbundledSubscriptions(cart.items);
	const availableBooks = OpsCart.getUnbundledBookSets(cart.items).filter(
		g => !g.includes("code")
	);

	if (simplified && availableBooks.length > 0 && availableSubs.length > 0) {
		const firstBook = availableBooks[0];
		const firstSub = availableSubs[0];
		const gradeNumber = OpsCart.getBundleInfo(firstBook).gradeNumber;
		const discount = firstBook === "bundle-grade-1" ? 48 : 60;
		const message = (
			<div>
				<p>
					You have a full set of Level {gradeNumber} books and a Yearly
					subscription in your cart. You will save ${discount} by bundling these
					items!
				</p>
				<div className={styles.bundle}>
					<CartItemIcon
						className={styles.bundleIcon}
						itemType="bundle"
						itemDetails={{level: gradeNumber}}
					/>
				</div>
			</div>
		);
		return (
			<ChooseOptionModal
				title="Bundle and Save"
				message={message}
				buttons={[
					{
						text: "Bundle",
						onClick: () =>
							makeBundle({selected: firstBook, additionalSelected: firstSub}),
						className: styles.bundleButton,
					},
				]}
				onClose={onClose}
			/>
		);
	}

	const numSubCodes = includeSubscriptionCodes
		? OpsCart.getUnbundledSubscriptionCodes(cart.items)[0]?.quantity
		: 0;
	const names = availableSubs.map(n => ({
		key: n,
		label: n + "'s Subscription",
		description: "",
	}));
	if (numSubCodes > 0) {
		names.push({
			// will not have conflicts with student names (which can only have letters)
			key: "beast-bulk-codes",
			label: `Subscription Code (${numSubCodes})`,
			description: "",
		});
	}
	const gradeSets = availableBooks.map(g => ({
		key: g,
		label: "BA " + OpsCart.getBundleInfo(g).gradeNumber + " Book Set",
		description: "",
	}));

	// find out if there is only one subscription type option for the user
	// to bundle
	// If one of these booleans is true, then the modal will only show one
	// Subscription option.
	const onlyHasSingleSubscription =
		availableSubs.length === 1 && numSubCodes === 0;
	const onlyHasSubscriptionCodes =
		availableSubs.length === 0 && numSubCodes > 0;

	const isSingleOption =
		(onlyHasSingleSubscription || onlyHasSubscriptionCodes) &&
		gradeSets.length === 1;
	const onlyHasLevel1BookSet =
		gradeSets.length === 1 && gradeSets[0].key === "bundle-grade-1";
	const bundleModalCopy = CopyTable.getBundleModalCopy(onlyHasLevel1BookSet);
	return (
		<SelectOptionModal
			title={bundleModalCopy.title}
			details={
				isSingleOption ? bundleModalCopy.singleOption : bundleModalCopy.details
			}
			options={gradeSets}
			additionalOptions={names}
			optionSubtitle="Choose a book set"
			additionalOptionSubtitle="Choose a subscription"
			defaultSelected={true}
			onSelect={makeBundle}
			buttonText={"Bundle"}
			onClose={onClose}
			hideCancel={true}
		/>
	);
};
BundleConversionModal.propTypes = {
	cart: PT.object.isRequired,
	makeBundle: PT.func.isRequired,
	onClose: PT.func.isRequired,
	includeSubscriptionCodes: PT.bool,
	simplified: PT.bool,
};
